import {
  InputButtonGroup,
  InputSelect,
  InputText,
  Button,
  FormField,
  FormRow,
} from "@endya-conformite/ui-kit";
import { ReactElement, useMemo, useState } from "react";
import {
  formeJuridiqueOptions,
  trancheEffectifOptions,
} from "@src/societes/form/EntrepriseIdentificationForm";
import {
  EntrepriseInfoActiviteFormValues,
  InputTitreAccessoire,
  categorieActiviteLabel,
} from "@src/societes/form/EntrepriseInfoActiviteForm";
import {
  EntrepriseSousCategoriesActiviteFormValues,
  sousCategoriesOptions,
} from "@src/societes/form/EntrepriseSousCategoriesActiviteForm";
import {
  GatewayEntreprise,
  GatewayInfoSocieteOrias,
} from "@conformite/gateway";
import { SocieteInformationFormValues } from "../useSocieteInformation";
import styles from "../SocieteInformation.module.scss";
import { SocieteInformationFormBloc } from "./SocieteInformationFormBloc";

type ShowActivitiesState = "no" | "warned" | "true";

type GeneralInformationProps = {
  categories: GatewayInfoSocieteOrias.Categorie[];
  categoriesWatched: GatewayInfoSocieteOrias.Categorie[] | undefined;
  needCOBSP: boolean;
  needMIOBSP: boolean;
};

export function GeneralInformation({
  categories,
  categoriesWatched,
  needCOBSP,
  needMIOBSP,
}: GeneralInformationProps) {
  const [showActivities, setShowActivities] =
    useState<ShowActivitiesState>("no");

  const categoriesOptions: {
    value: GatewayInfoSocieteOrias.Categorie;
    label: string;
    description?: ReactElement;
  }[] = useMemo(
    () => [
      {
        value: GatewayInfoSocieteOrias.Categorie.COA,
        label: categorieActiviteLabel[GatewayInfoSocieteOrias.Categorie.COA],
        description: categoriesWatched?.includes(
          GatewayInfoSocieteOrias.Categorie.COA
        ) ? (
          <InputTitreAccessoire name="coaAccessoire" />
        ) : undefined,
      },
      {
        value: GatewayInfoSocieteOrias.Categorie.MIA,
        label: categorieActiviteLabel[GatewayInfoSocieteOrias.Categorie.MIA],
        description: categoriesWatched?.includes(
          GatewayInfoSocieteOrias.Categorie.MIA
        ) ? (
          <InputTitreAccessoire name="miaAccessoire" />
        ) : undefined,
      },
      {
        value: GatewayInfoSocieteOrias.Categorie.COBSP,
        label: categorieActiviteLabel[GatewayInfoSocieteOrias.Categorie.COBSP],
        description: categoriesWatched?.includes(
          GatewayInfoSocieteOrias.Categorie.COBSP
        ) ? (
          <InputTitreAccessoire name="cobspAccessoire" />
        ) : undefined,
      },
      {
        value: GatewayInfoSocieteOrias.Categorie.MIOBSP,
        label: categorieActiviteLabel[GatewayInfoSocieteOrias.Categorie.MIOBSP],
        description: categoriesWatched?.includes(
          GatewayInfoSocieteOrias.Categorie.MIOBSP
        ) ? (
          <InputTitreAccessoire name="miobspAccessoire" />
        ) : undefined,
      },
    ],
    [categoriesWatched]
  );

  return (
    <SocieteInformationFormBloc title="Informations générales">
      <FormRow>
        <FormField>
          <InputText<SocieteInformationFormValues>
            label="Siren"
            name="siren"
            required
            disabled
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <InputText<SocieteInformationFormValues>
            label="Raison sociale"
            name="raisonSociale"
            required
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <InputSelect<
            SocieteInformationFormValues,
            GatewayEntreprise.TrancheEffectif
          >
            name="trancheEffectif"
            label="Tranche effectif"
            placeholder="Sélectionner"
            options={trancheEffectifOptions}
            required
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <InputSelect<
            SocieteInformationFormValues,
            GatewayEntreprise.FormeJuridique
          >
            name="formeJuridique"
            label="Forme juridique"
            placeholder="Sélectionner"
            options={formeJuridiqueOptions}
            required
          />
        </FormField>
      </FormRow>
      <FormRow className={styles.activites}>
        <FormField>
          {showActivities === "true" && (
            <InputButtonGroup<
              EntrepriseInfoActiviteFormValues,
              true,
              GatewayInfoSocieteOrias.Categorie
            >
              label={
                <b className={styles.label}>Vos catégories d&apos;adhésion</b>
              }
              options={categoriesOptions}
              name="categories"
              variant="outline-pop"
              size="large"
              isMultiple
            />
          )}
          {showActivities !== "true" && (
            <div>
              <b className={styles.label}>Vos catégories d&apos;adhésion</b>
              <ul>
                {categories.map((cat) => (
                  <li key={cat}>{categorieActiviteLabel[cat]}</li>
                ))}
              </ul>
              {showActivities === "warned" && (
                <p>
                  L&apos;ajout de catégorie(s) peut entrainer le règlement
                  d&apos;un complément de cotisation. Pour continuer, cliquez
                  sur
                  <Button
                    variant="text"
                    onClick={() => setShowActivities("true")}
                    className={styles.warningButton}
                  >
                    Confirmer
                  </Button>
                </p>
              )}

              {showActivities === "no" && (
                <Button onClick={() => setShowActivities("warned")}>
                  Modifier
                </Button>
              )}
            </div>
          )}
        </FormField>
      </FormRow>
      {(needMIOBSP || needCOBSP) && (
        <FormRow>
          <FormField>
            {needCOBSP && (
              <FormRow>
                <FormField>
                  <InputButtonGroup<
                    EntrepriseSousCategoriesActiviteFormValues,
                    true,
                    GatewayInfoSocieteOrias.ActiviteBancaire
                  >
                    options={sousCategoriesOptions}
                    name="sousCategoriesCOBSP"
                    label={
                      <b>
                        Sélectionnez au moins une sous-catégorie à votre
                        activité COBSP :
                      </b>
                    }
                    isMultiple
                    variant="outline-pop"
                    size="large"
                  />
                </FormField>
              </FormRow>
            )}

            {needMIOBSP && (
              <FormRow>
                <FormField>
                  <InputButtonGroup<
                    EntrepriseSousCategoriesActiviteFormValues,
                    true,
                    GatewayInfoSocieteOrias.ActiviteBancaire
                  >
                    options={sousCategoriesOptions}
                    name="sousCategoriesMIOBSP"
                    label={
                      <b>
                        Sélectionnez au moins une sous-catégorie à votre
                        activité MIOBSP :
                      </b>
                    }
                    variant="outline-pop"
                    size="large"
                    isMultiple
                  />
                </FormField>
              </FormRow>
            )}
          </FormField>
        </FormRow>
      )}
    </SocieteInformationFormBloc>
  );
}
